import { TutorialLayout, CodePreview } from '../../../../shared/components';
import { tutorialLevels } from '../../../../shared/config/tutorials';
import { Alert, AlertDescription } from '@voilajsx/uikit/alert';
import { Card, CardContent, CardHeader, CardTitle } from '@voilajsx/uikit/card';
import { Search, Image, Zap } from 'lucide-react';

const SEOPerformance = () => {
  return (
    <TutorialLayout levels={tutorialLevels}>
      <div className="space-y-8">
        <div>
          <h1 className="text-3xl font-bold mb-4">SEO & Performance</h1>
          <p className="text-lg text-muted-foreground">
            Optimize your UIKit application for search engines and performance.
          </p>
        </div>

        <Alert>
          <Zap className="w-4 h-4" />
          <AlertDescription>
            UIKit v1.4.0 includes built-in CLI commands for pre-rendering and image optimization.
          </AlertDescription>
        </Alert>

        <section className="space-y-4">
          <h2 className="text-2xl font-semibold flex items-center gap-2">
            <Search className="w-6 h-6" />
            Pre-rendering for SEO
          </h2>
          <p className="text-muted-foreground">
            SPAs serve an empty HTML shell, which search engines may not fully index.
            Pre-rendering generates static HTML for each route.
          </p>

          <CodePreview
            title="Basic Pre-render"
            code={`# Install puppeteer (required)
npm install puppeteer

# Pre-render the app
npm run prerender

# Or with options:
npx uikit prerender --dist dist --routes "/" --routes "/about"`}
            language="bash"
          />

          <Card>
            <CardHeader>
              <CardTitle className="text-lg">How Pre-rendering Works</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <ol className="list-decimal list-inside space-y-2 text-sm text-muted-foreground">
                <li>Puppeteer launches a headless browser</li>
                <li>Visits each route and waits for JavaScript to execute</li>
                <li>Captures the rendered HTML</li>
                <li>Saves it as a static HTML file</li>
              </ol>
            </CardContent>
          </Card>

          <CodePreview
            title="Pre-render Configuration"
            code={`# Create seo.config.json
{
  "routes": ["/", "/about", "/contact", "/blog"],
  "baseUrl": "https://myapp.com",
  "sitemap": true,
  "robotsTxt": true
}

# Run with config
npx uikit prerender --config seo.config.json`}
            language="json"
          />
        </section>

        <section className="space-y-4">
          <h2 className="text-2xl font-semibold">Pre-render Options</h2>

          <div className="bg-muted/50 rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left p-3 font-semibold">Option</th>
                  <th className="text-left p-3 font-semibold">Default</th>
                  <th className="text-left p-3 font-semibold">Description</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-border">
                  <td className="p-3 font-mono">--dist</td>
                  <td className="p-3 text-muted-foreground">dist</td>
                  <td className="p-3 text-muted-foreground">Build output directory</td>
                </tr>
                <tr className="border-b border-border">
                  <td className="p-3 font-mono">--routes</td>
                  <td className="p-3 text-muted-foreground">/</td>
                  <td className="p-3 text-muted-foreground">Routes to pre-render (comma-separated or multiple)</td>
                </tr>
                <tr className="border-b border-border">
                  <td className="p-3 font-mono">--port</td>
                  <td className="p-3 text-muted-foreground">4567</td>
                  <td className="p-3 text-muted-foreground">Server port for rendering</td>
                </tr>
                <tr className="border-b border-border">
                  <td className="p-3 font-mono">--config</td>
                  <td className="p-3 text-muted-foreground">seo.config.json</td>
                  <td className="p-3 text-muted-foreground">Config file path</td>
                </tr>
                <tr>
                  <td className="p-3 font-mono">--baseUrl</td>
                  <td className="p-3 text-muted-foreground">-</td>
                  <td className="p-3 text-muted-foreground">Base URL for canonical links</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        <section className="space-y-4">
          <h2 className="text-2xl font-semibold flex items-center gap-2">
            <Image className="w-6 h-6" />
            Image Optimization
          </h2>
          <p className="text-muted-foreground">
            Optimize images for faster loading with WebP conversion and compression.
          </p>

          <CodePreview
            title="Optimize Images"
            code={`# Install sharp (required)
npm install sharp

# Optimize images in public folder
npm run optimize

# Or with options:
npx uikit optimize --dir public --quality 80 --maxWidth 1200`}
            language="bash"
          />

          <Card>
            <CardHeader>
              <CardTitle className="text-lg">What It Does</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li>• Converts PNG/JPG images to WebP format</li>
                <li>• Compresses images to reduce file size</li>
                <li>• Resizes images to max width (preserving aspect ratio)</li>
                <li>• Keeps original files as fallback</li>
              </ul>
            </CardContent>
          </Card>
        </section>

        <section className="space-y-4">
          <h2 className="text-2xl font-semibold">Optimize Options</h2>

          <div className="bg-muted/50 rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left p-3 font-semibold">Option</th>
                  <th className="text-left p-3 font-semibold">Default</th>
                  <th className="text-left p-3 font-semibold">Description</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-border">
                  <td className="p-3 font-mono">--dir</td>
                  <td className="p-3 text-muted-foreground">public</td>
                  <td className="p-3 text-muted-foreground">Directory to scan for images</td>
                </tr>
                <tr className="border-b border-border">
                  <td className="p-3 font-mono">--quality</td>
                  <td className="p-3 text-muted-foreground">80</td>
                  <td className="p-3 text-muted-foreground">WebP quality (0-100)</td>
                </tr>
                <tr className="border-b border-border">
                  <td className="p-3 font-mono">--maxWidth</td>
                  <td className="p-3 text-muted-foreground">1200</td>
                  <td className="p-3 text-muted-foreground">Maximum width in pixels</td>
                </tr>
                <tr>
                  <td className="p-3 font-mono">--webp-only</td>
                  <td className="p-3 text-muted-foreground">false</td>
                  <td className="p-3 text-muted-foreground">Only generate WebP, skip PNG</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        <section className="space-y-4">
          <h2 className="text-2xl font-semibold">Meta Tags</h2>

          <CodePreview
            title="SEO Component"
            code={`// src/components/SEO.tsx
import { Helmet } from 'react-helmet-async';

interface SEOProps {
  title: string;
  description: string;
  image?: string;
  url?: string;
}

export function SEO({ title, description, image, url }: SEOProps) {
  const siteTitle = 'My App';
  const fullTitle = \`\${title} | \${siteTitle}\`;

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />

      {/* Open Graph */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      {image && <meta property="og:image" content={image} />}
      {url && <meta property="og:url" content={url} />}

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
    </Helmet>
  );
}`}
            language="tsx"
          />

          <CodePreview
            title="Usage"
            code={`// In a page component
import { SEO } from '../components/SEO';

function AboutPage() {
  return (
    <>
      <SEO
        title="About Us"
        description="Learn about our company and mission."
        url="https://myapp.com/about"
      />
      <div>
        {/* Page content */}
      </div>
    </>
  );
}`}
            language="tsx"
          />
        </section>

        <section className="space-y-4">
          <h2 className="text-2xl font-semibold">Performance Tips</h2>

          <div className="space-y-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg">1. Code Splitting</CardTitle>
              </CardHeader>
              <CardContent>
                <CodePreview
                  code={`// Lazy load pages
const Dashboard = lazy(() => import('./pages/Dashboard'));

<Suspense fallback={<Loading />}>
  <Dashboard />
</Suspense>`}
                  language="tsx"
                />
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg">2. Bundle Analysis</CardTitle>
              </CardHeader>
              <CardContent>
                <CodePreview
                  code={`# Analyze bundle size
npm run build
npx vite-bundle-visualizer`}
                  language="bash"
                />
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg">3. Asset Optimization</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>• Use WebP images (npm run optimize)</li>
                  <li>• Lazy load images below the fold</li>
                  <li>• Use appropriate image sizes</li>
                  <li>• Enable gzip/brotli compression on server</li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </section>

        <section className="space-y-4">
          <h2 className="text-2xl font-semibold">Production Workflow</h2>

          <CodePreview
            title="Complete Build & Optimize"
            code={`# 1. Build the app
npm run build

# 2. Optimize images
npm run optimize

# 3. Pre-render for SEO
npm run prerender

# 4. Deploy
npm run deploy:gh  # or vercel, netlify, etc.`}
            language="bash"
          />
        </section>
      </div>
    </TutorialLayout>
  );
};

export default SEOPerformance;
